import { AwaitContainer, Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@ibsys/kit';
import { ArrowLeftIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  C1_BOT_CODE,
  C1_TO_ASR_CODE,
  C2_BITRIX_CODE,
  C3_ASR_DEMO_CODE,
  S1_ANONYMIZER_CODE,
  useIntegration,
} from '@/entities/integrations';
import { PageContainer } from '@/shared/ui';

import { AnonymizerSection } from './AnonymizerSection';
import { BitrixCallsSection } from './BitrixCallsSection';
import { BitrixTouchSection } from './BitrixTouchSection';
import { C1BotSection } from './C1BotSection';
import { C1ToAsrSection } from './C1ToAsrSection';
import { C3AsrDemoSection } from './C3AsrDemoSection';
import { EnvVariablesTab } from './EnvVariablesTab';
import { GeneralInfoTab } from './GeneralInfoTab';
import { LoggingTab } from './LoggingTab';
import { TestingTab } from './TestingTab';

const TAB_VALUES = ['general', 'env', 'testing', 'logging'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const isTabValue = (value: string | null): value is TabValue =>
  value !== null && (TAB_VALUES as readonly string[]).includes(value);

export const IntegrationDetailPage: FC = () => {
  const { t } = useTranslation(['integrations', 'common']);
  const navigate = useNavigate();
  const { code } = useParams<{ code: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab: TabValue = isTabValue(searchParams.get('tab')) ? (searchParams.get('tab') as TabValue) : 'general';
  const [activeTab, setActiveTab] = useState<TabValue>(initialTab);

  const { data, isLoading, isError } = useIntegration(code);

  const handleBack = () => {
    navigate('/integrations/list');
  };

  const handleTabChange = (value: string) => {
    if (!isTabValue(value)) return;
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <PageContainer enableScroll className="flex flex-col h-full gap-4 w-full">
      <Button variant="ghost" size="sm" onClick={handleBack} className="self-start">
        <ArrowLeftIcon className="size-4" />
        {t('integrations:detail.back')}
      </Button>

      <AwaitContainer isLoading={isLoading} className="flex-1">
        {isError || !data ? (
          <div className="text-muted-foreground">{t('common:emptyTable')}</div>
        ) : (
          <div className="flex flex-col gap-4 max-w-5xl">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList>
                <TabsTrigger value="general">{t('integrations:tabs.general')}</TabsTrigger>
                <TabsTrigger value="env">{t('integrations:tabs.envVariables')}</TabsTrigger>
                <TabsTrigger value="testing">{t('integrations:tabs.testing')}</TabsTrigger>
                <TabsTrigger value="logging">{t('integrations:tabs.logging')}</TabsTrigger>
              </TabsList>
              <TabsContent value="general" className="mt-4">
                <GeneralInfoTab integration={data} />
              </TabsContent>
              <TabsContent value="env" className="mt-4">
                <EnvVariablesTab envVariables={data.envVariables ?? []} />
              </TabsContent>
              <TabsContent value="testing" className="mt-4">
                <TestingTab integration={data} />
              </TabsContent>
              <TabsContent value="logging" className="mt-4">
                <LoggingTab code={data.code} />
              </TabsContent>
            </Tabs>

            {/* Дополнительные элементы управления для интеграции c2_bitrix24 */}
            {data.code === C2_BITRIX_CODE && <BitrixCallsSection />}

            {/* Блок режима касания для интеграции c2_bitrix24 */}
            {data.code === C2_BITRIX_CODE && <BitrixTouchSection envVariables={data.envVariables ?? []} />}

            {/* Переход к настройке конфигурации для интеграции анонимизатора */}
            {data.code === S1_ANONYMIZER_CODE && <AnonymizerSection />}

            {/* Блок загрузки готовых ASR-транскрибаций для интеграции c3_asr_demo */}
            {data.code === C3_ASR_DEMO_CODE && <C3AsrDemoSection />}

            {/* Блок прогрузки диалогов голосового бота для интеграции c1_bot */}
            {data.code === C1_BOT_CODE && <C1BotSection />}

            {/* Блок отбора звонков и отправки их на транскрибацию для интеграции c1_to_asr */}
            {data.code === C1_TO_ASR_CODE && <C1ToAsrSection />}
          </div>
        )}
      </AwaitContainer>
    </PageContainer>
  );
};
